import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import CloseIcon from '@material-ui/icons/Close'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import SearchIcon from '@material-ui/icons/Search'
import VisibilityIcon from '@material-ui/icons/Visibility'

const HMI_GREEN = '#2b7a2f'
const HMI_ORANGE = '#d86f00'
const LOGO_URL = '/build/640a56404965b71a9edd0e7d_Logo.png'

const dateInputValue = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const previousMonth = () => {
  const now = new Date()
  return {
    startDate: dateInputValue(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    endDate: dateInputValue(new Date(now.getFullYear(), now.getMonth(), 0))
  }
}

const currency = (value) => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const displayDate = value => value
  ? new Date(`${value}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  })
  : '—'

const monthName = (key) => new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC'
}).format(new Date(`${key}-01T00:00:00Z`))

const monthKeys = (startDate, endDate) => {
  const keys = []
  const endKey = endDate.slice(0, 7)
  let cursor = new Date(`${startDate.slice(0, 7)}-01T00:00:00Z`)
  while (cursor.toISOString().slice(0, 7) <= endKey) {
    keys.push(cursor.toISOString().slice(0, 7))
    cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1))
  }
  return keys
}

const monthCells = (key) => {
  const [year, month] = key.split('-').map(Number)
  const first = new Date(Date.UTC(year, month - 1, 1))
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const cells = Array(first.getUTCDay()).fill(null)
  for (let day = 1; day <= days; day += 1) cells.push(day)
  while (cells.length % 7) cells.push(null)
  return cells
}

const SummaryCard = ({ label, value, accent = HMI_GREEN }) => (
  <Card variant="outlined" style={{ minWidth: 175, flex: '1 1 175px', borderTop: `4px solid ${accent}` }}>
    <CardContent style={{ paddingBottom: 14 }}>
      <Typography variant="caption" style={{ color: '#6f796e', fontWeight: 700, letterSpacing: 0.8 }}>
        {label.toUpperCase()}
      </Typography>
      <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 700, marginTop: 4 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
)

const VisitCalendar = ({ snapshot }) => {
  const byDate = useMemo(
    () => new Map((snapshot.calendar || []).map(day => [day.date, day.properties || []])),
    [snapshot]
  )
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  return (
    <Box>
      {monthKeys(snapshot.period.startDate, snapshot.period.endDate).map(key => {
        const [year, month] = key.split('-').map(Number)
        return (
          <Box key={key} mb={4}>
            <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 700, marginBottom: 12 }}>
              {monthName(key)}
            </Typography>
            <Box
              display="grid"
              style={{
                gridTemplateColumns: 'repeat(7, minmax(115px, 1fr))',
                borderTop: `3px solid ${HMI_GREEN}`,
                borderLeft: '1px solid #ccd3c9',
                minWidth: 805
              }}
            >
              {weekdays.map(day => (
                <Box
                  key={day}
                  style={{
                    padding: '8px 6px',
                    background: HMI_GREEN,
                    color: '#fff',
                    borderRight: '1px solid rgba(255,255,255,.22)',
                    fontWeight: 700,
                    textAlign: 'center',
                    fontSize: 12
                  }}
                >
                  {day}
                </Box>
              ))}
              {monthCells(key).map((day, index) => {
                const date = day
                  ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                  : ''
                const properties = date ? byDate.get(date) || [] : []
                return (
                  <Box
                    key={`${key}-${index}`}
                    style={{
                      minHeight: 122,
                      padding: 8,
                      background: day ? (properties.length ? '#f5f9f1' : '#fff') : '#f2f3ef',
                      borderRight: '1px solid #ccd3c9',
                      borderBottom: '1px solid #ccd3c9'
                    }}
                  >
                    {day && (
                      <>
                        <Typography style={{ color: HMI_ORANGE, fontWeight: 800, fontSize: 13 }}>
                          {day}
                        </Typography>
                        <Box mt={0.5}>
                          {properties.map(property => (
                            <Chip
                              key={`${property.propertyId}-${property.communityName}`}
                              component="a"
                              href={property.aspirePropertyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              size="small"
                              label={property.buildingNumber || property.propertyId}
                              title={`Open ${property.propertyName} in Aspire`}
                              style={{
                                height: 21,
                                margin: '2px 3px 2px 0',
                                background: '#dfeeda',
                                color: '#234c26',
                                fontWeight: 700,
                                fontSize: 11
                              }}
                            />
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

const VisitDetailsDialog = ({ row, onClose }) => (
  <Dialog open={!!row} onClose={onClose} fullWidth maxWidth="md">
    {row && (
      <>
        <DialogTitle disableTypography style={{ borderTop: `5px solid ${HMI_GREEN}`, paddingBottom: 12 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box pr={2}>
              <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 800 }}>
                Visit Details
              </Typography>
              <Typography variant="subtitle1" style={{ color: '#3f4a3e', marginTop: 3 }}>
                {row.buildingName}
              </Typography>
              <Box mt={1}>
                <Chip size="small" label={`Property ${row.propertyId}`} style={{ marginRight: 6 }} />
                <Chip size="small" label={`${row.visitCount} completed visits`} style={{ marginRight: 6 }} />
                <Chip size="small" label={`${row.serviceDateCount} service dates`} />
              </Box>
            </Box>
            <IconButton aria-label="Close visit details" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="body2" color="textSecondary">
              These completed Aspire WorkTickets produce this property’s calendar and visit count.
            </Typography>
            {row.aspirePropertyUrl && (
              <Button
                component="a"
                href={row.aspirePropertyUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                startIcon={<OpenInNewIcon />}
                style={{ color: HMI_GREEN, whiteSpace: 'nowrap' }}
              >
                Open Aspire Property
              </Button>
            )}
          </Box>
          {row.billingBasis === 'Per visit' && (
            <Paper variant="outlined" style={{ padding: 14, marginBottom: 16, background: '#f8faf6' }}>
              <Typography variant="subtitle2" style={{ color: HMI_GREEN, fontWeight: 800 }}>
                Billing reconciliation
              </Typography>
              <Box display="flex" flexWrap="wrap" mt={1} style={{ gap: 7 }}>
                <Chip size="small" label={`${row.visitCount} completed`} />
                <Chip size="small" label={`${row.billedTicketCount || 0} billed`} />
                <Chip size="small" label={`Invoice ${currency(row.monthlyTotal)}`} />
                {row.invoiceAveragePerCompletedVisit != null && (
                  <Chip size="small" label={`${currency(row.invoiceAveragePerCompletedVisit)} / completed visit`} />
                )}
              </Box>
              {!!(row.billedRateBreakdown || []).length && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 9 }}>
                  Billed WorkTicket rates:{' '}
                  {row.billedRateBreakdown
                    .map(item => `${item.count} × ${currency(item.rate)}`)
                    .join(' + ')}
                </Typography>
              )}
            </Paper>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow style={{ background: HMI_GREEN }}>
                  {['Visit Date', 'Work Ticket', 'Service', 'Route', 'Status'].map(header => (
                    <TableCell key={header} style={{ color: '#fff', fontWeight: 700 }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(row.visitDetails || []).map(visit => (
                  <TableRow key={visit.workTicketId}>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {(visit.visitDates || []).map(displayDate).join(', ') || displayDate(visit.completeDate)}
                    </TableCell>
                    <TableCell>
                      {visit.workTicketNumber ? `#${visit.workTicketNumber}` : `ID ${visit.workTicketId}`}
                    </TableCell>
                    <TableCell>{visit.serviceName}</TableCell>
                    <TableCell>{visit.routeName || '—'}</TableCell>
                    <TableCell>{visit.statusName || 'Completed'}</TableCell>
                  </TableRow>
                ))}
                {!(row.visitDetails || []).length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No visit details were returned.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </>
    )}
  </Dialog>
)

const ScheduleTable = ({ snapshot, onViewDetails }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow style={{ background: HMI_GREEN }}>
          {['Building #', 'Building Name', 'Building ID', 'Preferred Day', 'Visits', 'Per Visit Fee', 'Monthly Total', '']
            .map((header, index) => (
              <TableCell
                key={`${header}-${index}`}
                align={index >= 4 && index <= 6 ? 'right' : 'left'}
                style={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {header}
              </TableCell>
            ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {(snapshot.schedule || []).map(row => (
          <TableRow key={`${row.propertyId}-${row.buildingName}`} hover>
            <TableCell>
              {row.aspirePropertyUrl
                ? (
                  <Link
                    href={row.aspirePropertyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: HMI_GREEN, fontWeight: 700 }}
                  >
                    {row.communityName || '—'}
                  </Link>
                  )
                : row.communityName || '—'}
            </TableCell>
            <TableCell style={{ minWidth: 280 }}>
              {row.aspirePropertyUrl
                ? (
                  <Link
                    href={row.aspirePropertyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#263126' }}
                  >
                    {row.buildingName}
                  </Link>
                  )
                : row.buildingName}
            </TableCell>
            <TableCell style={{ fontWeight: 700, color: HMI_GREEN }}>
              {row.legacyClientId && row.legacyJobSiteId
                ? (
                  <Link
                    href={`/clients/${row.legacyClientId}/JobSite/${row.legacyJobSiteId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.buildingNumber ? undefined : 'Building ID is blank — open the legacy jobsite to set it'}
                    style={{ color: row.buildingNumber ? HMI_GREEN : '#b3541e', fontWeight: 700 }}
                  >
                    {row.buildingNumber || '—'}
                  </Link>
                  )
                : row.buildingNumber || '—'}
            </TableCell>
            <TableCell>{row.preferredVisitDay || '—'}</TableCell>
            <TableCell align="right">{row.visitCount}</TableCell>
            <TableCell align="right">
              {row.billingBasis === 'Monthly' || row.perVisitFee == null ? 'Monthly' : currency(row.perVisitFee)}
            </TableCell>
            <TableCell align="right" style={{ fontWeight: 700 }}>{currency(row.monthlyTotal)}</TableCell>
            <TableCell align="right">
              <Button
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => onViewDetails(row)}
                style={{ color: HMI_GREEN, whiteSpace: 'nowrap' }}
              >
                Details
              </Button>
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell colSpan={4} style={{ fontWeight: 800, color: HMI_GREEN }}>Totals</TableCell>
          <TableCell align="right" style={{ fontWeight: 800 }}>{snapshot.summary.visitCount}</TableCell>
          <TableCell />
          <TableCell align="right" style={{ fontWeight: 800, color: HMI_GREEN }}>
            {currency(snapshot.summary.invoiceTotal)}
          </TableCell>
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
)

const MaintenanceInvoicePacketPage = ({ url }) => {
  const defaults = useMemo(previousMonth, [])
  const [invoiceBatchId, setInvoiceBatchId] = useState('')
  const [startDate, setStartDate] = useState(defaults.startDate)
  const [endDate, setEndDate] = useState(defaults.endDate)
  const [snapshot, setSnapshot] = useState(null)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [detailRow, setDetailRow] = useState(null)
  const [debugLinks, setDebugLinks] = useState(false)
  const [error, setError] = useState('')
  const endpoint = `${url}/api/bid-estimation/maintenance-invoice-packets`

  const payload = () => ({
    invoiceBatchId: Number(invoiceBatchId),
    startDate,
    endDate
  })

  const generatePreview = async event => {
    if (event) event.preventDefault()
    setLoading(true)
    setError('')
    setSnapshot(null)
    try {
      const response = await axios.post(`${endpoint}/preview`, payload())
      setSnapshot(response.data)
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message)
    } finally {
      setLoading(false)
    }
  }

  const downloadPdf = async () => {
    setDownloading(true)
    setError('')
    try {
      const response = await axios.post(`${endpoint}/pdf`, { ...payload(), debugLinks }, { responseType: 'blob' })
      const disposition = response.headers['content-disposition'] || ''
      const name = disposition.match(/filename="([^"]+)"/)?.[1] ||
        `maintenance-invoice-packet-${invoiceBatchId}.pdf`
      const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = name
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000)
    } catch (requestError) {
      if (requestError.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await requestError.response.data.text())
          setError(body.error || requestError.message)
        } catch (parseError) {
          setError(requestError.message)
        }
      } else {
        setError(requestError.response?.data?.error || requestError.message)
      }
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Container maxWidth="xl" style={{ paddingTop: 28, paddingBottom: 64 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <img src={LOGO_URL} alt="HMI" style={{ width: 170, maxHeight: 64, objectFit: 'contain', objectPosition: 'left center' }} />
        <Box ml={3} pl={3} style={{ borderLeft: `3px solid ${HMI_ORANGE}` }}>
          <Typography variant="h4" style={{ color: HMI_GREEN, fontWeight: 800 }}>
            Maintenance Invoice Packets
          </Typography>
          <Typography variant="body2" style={{ color: '#6d776b', marginTop: 4 }}>
            Generate visit calendars and invoice schedules directly from Aspire.
          </Typography>
        </Box>
        <Box ml="auto">
          <Button
            href="/maintenance-billing-forecast"
            variant="outlined"
            style={{ color: HMI_GREEN, borderColor: HMI_GREEN }}
          >
            Billing Forecast
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" style={{ padding: 22, borderTop: `5px solid ${HMI_GREEN}` }}>
        <form onSubmit={generatePreview}>
          <Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{ gap: 16 }}>
            <TextField
              required
              variant="outlined"
              label="Invoice Batch ID"
              value={invoiceBatchId}
              onChange={event => setInvoiceBatchId(event.target.value.replace(/\D/g, ''))}
              helperText="Aspire InvoiceBatchID"
              inputProps={{ inputMode: 'numeric', style: { width: 150 } }}
            />
            <TextField
              required
              variant="outlined"
              type="date"
              label="Billing period starts"
              value={startDate}
              onChange={event => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              required
              variant="outlined"
              type="date"
              label="Billing period ends"
              value={endDate}
              onChange={event => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={17} color="inherit" /> : <SearchIcon />}
              disabled={loading || !invoiceBatchId || !startDate || !endDate}
              style={{ height: 56, paddingLeft: 22, paddingRight: 22, background: HMI_GREEN, color: '#fff' }}
            >
              {loading ? 'Loading Aspire…' : 'Build Preview'}
            </Button>
            {snapshot && (
              <>
                <Button
                  variant="contained"
                  startIcon={downloading ? <CircularProgress size={17} color="inherit" /> : <PictureAsPdfIcon />}
                  disabled={downloading}
                  onClick={downloadPdf}
                  style={{ height: 56, paddingLeft: 22, paddingRight: 22, background: HMI_ORANGE, color: '#fff' }}
                >
                  {downloading ? 'Building PDF…' : 'Download PDF'}
                </Button>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={debugLinks}
                      onChange={event => setDebugLinks(event.target.checked)}
                      size="small"
                    />
                  )}
                  label="Debug links in PDF"
                  title="Off: the PDF only has internal table-of-contents links. On: schedule and calendar entries also hyperlink to Aspire."
                  style={{ marginLeft: 4, color: debugLinks ? HMI_ORANGE : '#788176' }}
                />
              </>
            )}
          </Box>
        </form>
        <Typography variant="caption" style={{ display: 'block', marginTop: 12, color: '#788176' }}>
          Packets are stateless. Rebuilding always uses the current Aspire batch and the latest synced visit data.
        </Typography>
      </Paper>

      {error && <Box mt={2}><Alert severity="error">{error}</Alert></Box>}

      {snapshot && (
        <Box mt={3}>
          <Paper
            elevation={0}
            style={{ padding: '18px 22px', marginBottom: 18, background: '#f3f8ef', borderLeft: `5px solid ${HMI_ORANGE}` }}
          >
            <Box display="flex" alignItems="baseline" justifyContent="space-between" flexWrap="wrap">
              <Box>
                <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 800 }}>
                  Invoice Batch {snapshot.batch.invoiceBatchNumber}
                </Typography>
                <Typography variant="body1" style={{ color: '#3f4a3e', marginTop: 4 }}>
                  {snapshot.batch.customerName} · {snapshot.period.label}
                </Typography>
              </Box>
              {snapshot.source.latestVisitSync && (
                <Typography variant="caption" style={{ color: '#6d776b' }}>
                  Visits synced {new Date(snapshot.source.latestVisitSync).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Paper>

          <Box display="flex" flexWrap="wrap" mb={3} style={{ gap: 14 }}>
            <SummaryCard label="Properties" value={snapshot.summary.propertyCount} />
            <SummaryCard label="Completed visits" value={snapshot.summary.visitCount} />
            <SummaryCard label="Service dates" value={snapshot.summary.serviceDateCount} />
            <SummaryCard label="Invoice total" value={currency(snapshot.summary.invoiceTotal)} accent={HMI_ORANGE} />
          </Box>

          {(snapshot.summary.excludedIrrigationTickets > 0 || snapshot.summary.excludedHerbicideTickets > 0) && (
            <Box mb={2}>
              <Alert severity="info">
                Excluded {snapshot.summary.excludedIrrigationTickets} irrigation and{' '}
                {snapshot.summary.excludedHerbicideTickets} herbicide ticket
                {snapshot.summary.excludedIrrigationTickets + snapshot.summary.excludedHerbicideTickets === 1 ? '' : 's'}.
              </Alert>
            </Box>
          )}

          {(snapshot.warnings || []).length > 0 && (
            <Box mb={3}>
              <Alert severity="warning">
                <Typography variant="subtitle2">Review before downloading</Typography>
                {(snapshot.warnings || []).map((warning, index) => (
                  <Typography
                    key={`${warning}-${index}`}
                    variant="body2"
                    style={{ marginTop: index ? 7 : 3, lineHeight: 1.45 }}
                  >
                    • {warning}
                  </Typography>
                ))}
              </Alert>
            </Box>
          )}

          <Paper variant="outlined" style={{ padding: 22, marginBottom: 24, overflowX: 'auto' }}>
            <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 800, marginBottom: 4 }}>
              Monthly Visit Calendar
            </Typography>
            <Typography variant="body2" style={{ color: '#6d776b', marginBottom: 20 }}>
              Calendar labels are Building IDs. Click any property to open it directly in Aspire.
            </Typography>
            <VisitCalendar snapshot={snapshot} />
          </Paper>

          <Paper variant="outlined" style={{ padding: 22 }}>
            <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 800, marginBottom: 4 }}>
              Maintenance Schedule
            </Typography>
            <Typography variant="body2" style={{ color: '#6d776b', marginBottom: 16 }}>
              Visit counts are unique completed recurring-maintenance WorkTickets. Invoice totals remain authoritative.
            </Typography>
            <ScheduleTable snapshot={snapshot} onViewDetails={setDetailRow} />
          </Paper>
        </Box>
      )}
      <VisitDetailsDialog row={detailRow} onClose={() => setDetailRow(null)} />
    </Container>
  )
}

const mapStateToProps = state => ({ url: state.url })

export default connect(mapStateToProps)(MaintenanceInvoicePacketPage)
